import React, { useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  EmailOutlined,
  LockOutlined,
  ArrowForward
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for dark theme
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '32px',
  background: '#111111',
  border: '1px solid rgba(255,255,255,0.05)',
  width: '100%',
  maxWidth: '420px',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #00FF87 0%, #60EFFF 100%)',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
  color: '#000000',
  padding: '12px 20px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '16px',
  boxShadow: '0 8px 20px rgba(0, 255, 135, 0.2)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(0, 255, 135, 0.3)',
    background: 'linear-gradient(135deg, #00E077 0%, #50DFFF 100%)',
  },
  '&:disabled': {
    background: 'rgba(255,255,255,0.1)',
    boxShadow: 'none',
    color: '#666',
  }
}));

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '16px',
    backgroundColor: '#1A1A1A',
    border: '1px solid rgba(255,255,255,0.05)',
    '&:hover': {
      backgroundColor: '#1F1F1F',
      borderColor: 'rgba(255,255,255,0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: '#1A1A1A',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#00FF87',
        borderWidth: '2px',
      }
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    color: '#666',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00FF87',
  },
  '& .MuiOutlinedInput-input': {
    color: '#FFFFFF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.05)',
  }
});

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const { data } = await API.post('/auth/login', { email, password });
      login(data.token, data.user);
      
      // Redirect based on role
      if (data.user?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container 
      component="main" 
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0A0A',
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 255, 135, 0.03) 0%, transparent 50%)',
        padding: 2
      }}
    >
      <StyledPaper elevation={0}>
        {/* Logo Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(135deg, #00FF87 0%, #60EFFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              fontSize: '2rem'
            }}
          >
            GantryFX
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              fontSize: '0.85rem'
            }}
          >
            Recover it
          </Typography>
        </Box>

        <Typography 
          variant="h5" 
          component="h1" 
          align="center"
          sx={{ 
            fontWeight: 700, 
            color: '#FFFFFF',
            mb: 1
          }}
        >
          Welcome Back
        </Typography>
        <Typography 
          variant="body2" 
          align="center"
          sx={{ 
            color: '#666',
            mb: 3,
            fontSize: '0.9rem'
          }}
        >
          Sign in to access your portfolio
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2.5, 
              borderRadius: '12px',
              backgroundColor: 'rgba(255, 68, 68, 0.1)',
              color: '#FF4444',
              py: 1,
              '& .MuiAlert-icon': {
                color: '#FF4444',
                fontSize: '1.2rem'
              },
              '& .MuiAlert-message': {
                fontSize: '0.85rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <StyledTextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlined sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your email"
            />

            <StyledTextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#666', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your password"
            />

            <StyledButton
              type="submit"
              fullWidth
              disabled={loading}
              endIcon={!loading && <ArrowForward />}
              sx={{ mt: 1 }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: '#000000' }} />
              ) : (
                'Sign In'
              )}
            </StyledButton>
          </Box>
        </form>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
            Don't have an account?{' '}
            <Typography
              component={Link}
              to="/register"
              variant="body2"
              sx={{
                color: '#00FF87',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              Create Account
            </Typography>
          </Typography>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#444',
              display: 'block',
              fontSize: '0.7rem'
            }}
          >
            © {new Date().getFullYear()} GantryFX. All rights reserved.
          </Typography>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default LoginPage;